import React, { useState, useEffect } from "react";
import { HelmetProvider } from "react-helmet-async";
import { AuthenticatedTemplate, UnauthenticatedTemplate, useMsal, useIsAuthenticated } from "@azure/msal-react";
import { Box, Button as MuiButton, Container as MuiContainer, Grid, Stack, Typography } from "@mui/material";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import { PageLayout } from "./components/landing/PageLayout";
import { Button } from "react-bootstrap";
import { Shop } from '../src/components/shop/Shop';
import { SignInButton } from "./components/common/SignInButton";
import { RequestInterceptor } from "./components/common/RequestInterceptor";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { ErrorBoundary } from "react-error-boundary";
import { ShopBasket } from "./components/shop/ShopBasket";
import { ShopItemPage } from "./components/shop/ShopItemPage";
import { ProfileContent } from "./components/profile/ProfileContent";
import { Orders } from "./components/orders/Orders";
import { PendingOrders } from "./components/pendingOrders/PendingOrders";
import { Invoices } from "./components/invoices/Invoices";
import { CartProvider } from "./context/CartContext";
import ThemeProvider from "./theme";

const BRAND_LOGOS = [
  { src: "assets/images/mts/alcoa-w1.png", alt: "Alcoa" },
  { src: "assets/images/mts/michelin-w.png", alt: "Michelin" },
  { src: "assets/images/mts/goodyear-w.png", alt: "Goodyear" },
  { src: "assets/images/mts/dunlop-w.png", alt: "Dunlop" },
  { src: "assets/images/mts/torque.png", alt: "Torque" },
  { src: "assets/images/mts/bridgestone-w.png", alt: "Bridgestone" },
];



/**
 * If a user is authenticated the ProfileContent component above is rendered. Otherwise a message indicating a user is not authenticated is rendered.
 */
const MainContent = (props) => {

    let isAuthenticated = useIsAuthenticated();
    let navigate = useNavigate();
    let location = useLocation();

    useEffect(() => {
        // Only redirect on initial auth (when arriving at the root path), never
        // again — otherwise transient MSAL state changes during downloads or
        // token refresh can yank the user back to /shop while on another page.
        if (isAuthenticated && (location.pathname === "/" || location.pathname === "")) {
            navigate("/shop", { replace: true });
        }
    }, [isAuthenticated]);


    function ErrorFallback({ error, resetErrorBoundary }) {
        return (
            <div role="alert">
                <p>Something went wrong:</p>
                <pre>{error.message}</pre>
                <Button onClick={resetErrorBoundary} variant="primary" >Try again</Button>
            </div>
        )
    }

    const [hasError, setHasError] = React.useState(false)
    return (
        <div className="App">

            <AuthenticatedTemplate>
                <RequestInterceptor>
                    <ErrorBoundary FallbackComponent={ErrorFallback}
                        onReset={() => setHasError(false)}
                        resetKeys={[hasError]}>
                        {
                            hasError ? null :
                                <Routes>
                                    <Route path="shop" element={<Shop />} />
                                    <Route path="basket" element={<ShopBasket />} />
                                    <Route path="item/:id" element={<ShopItemPage />} />
                                    <Route path="profile" element={<ProfileContent />} />
                                    <Route path="pendingorders" element={<PendingOrders />} />
                                    <Route path="orders" element={<Orders />} />
                                    <Route path="invoices" element={<Invoices />} />
                                    {/* <Route path="shop/checkout" element={<ShopCheckout />} /> */}
                                </Routes>
                        }
                    </ErrorBoundary>
                </RequestInterceptor>
            </AuthenticatedTemplate>

            <UnauthenticatedTemplate>
                <Box
                    sx={{
                        position: "relative",
                        minHeight: "100vh",
                        overflow: "hidden",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                    }}
                >
                    <Box
                        component="video"
                        autoPlay
                        muted
                        loop
                        playsInline
                        sx={{
                            position: "fixed",
                            inset: 0,
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            zIndex: 0,
                        }}
                    >
                        <source src="https://www.midlandtyres.ie/images/mts/michelin.mp4" type="video/mp4" />
                    </Box>
                    <Box
                        sx={{
                            position: "fixed",
                            inset: 0,
                            backgroundColor: "rgba(0, 0, 0, 0.55)",
                            zIndex: 1,
                        }}
                    />

                    <MuiContainer maxWidth="md" sx={{ position: "relative", zIndex: 2, py: 8 }}>
                        <Stack
                            spacing={4}
                            alignItems="center"
                            sx={{
                                color: "common.white",
                                textAlign: "center",
                                backgroundColor: "rgba(0, 0, 0, 0.45)",
                                backdropFilter: "blur(8px)",
                                borderRadius: 3,
                                border: "1px solid rgba(255,255,255,0.08)",
                                p: { xs: 4, md: 6 },
                            }}
                        >
                            <Box
                                sx={{
                                    width: 96,
                                    height: 96,
                                    borderRadius: "50%",
                                    bgcolor: "#F5A623",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    boxShadow: "0 8px 24px rgba(0,0,0,0.25)",
                                }}
                            >
                                <Box
                                    component="img"
                                    src="/assets/images/midland-tyres-logo.png"
                                    alt="Midland Tyre Services"
                                    sx={{ width: 64, height: 64, objectFit: "contain" }}
                                />
                            </Box>
                            <Typography variant="h3" sx={{ fontWeight: 800, lineHeight: 1.15 }}>
                                Welcome to Midland Tyre Services
                            </Typography>
                            <Typography variant="body1" sx={{ maxWidth: 540, opacity: 0.92 }}>
                                Please sign in with your trade account to browse the catalogue, place orders and review your invoices.
                            </Typography>
                            <SignInButton />
                            <Stack
                                direction={{ xs: "column", sm: "row" }}
                                spacing={3}
                                divider={
                                    <Box
                                        sx={{
                                            width: { xs: "100%", sm: "1px" },
                                            height: { xs: "1px", sm: 20 },
                                            backgroundColor: "rgba(255,255,255,0.3)",
                                        }}
                                    />
                                }
                                sx={{ pt: 1 }}
                            >
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <PhoneOutlinedIcon fontSize="small" />
                                    <Typography
                                        component="a"
                                        href="tel:+35350541102"
                                        sx={{ color: "common.white", textDecoration: "none", "&:hover": { color: "primary.main" } }}
                                    >
                                        +353 (505) 41 102
                                    </Typography>
                                </Stack>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <EmailOutlinedIcon fontSize="small" />
                                    <Typography
                                        component="a"
                                        href="mailto:info@midlandtyres.ie"
                                        sx={{ color: "common.white", textDecoration: "none", "&:hover": { color: "primary.main" } }}
                                    >
                                        info@midlandtyres.ie
                                    </Typography>
                                </Stack>
                            </Stack>
                        </Stack>

                        <Grid container spacing={3} sx={{ mt: 6 }} justifyContent="center" alignItems="center">
                            {BRAND_LOGOS.map((l) => (
                                <Grid item key={l.alt} xs={4} sm={2}>
                                    <Box
                                        component="img"
                                        src={l.src}
                                        alt={l.alt}
                                        sx={{
                                            maxWidth: "100%",
                                            maxHeight: 80,
                                            mx: "auto",
                                            display: "block",
                                            opacity: 0.85,
                                            filter: "brightness(0) invert(1)",
                                            transition: "opacity 0.2s",
                                            "&:hover": { opacity: 1 },
                                        }}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    </MuiContainer>
                </Box>
            </UnauthenticatedTemplate>
        </div>
    );
};


export default function App() {
    return (
        <HelmetProvider>
            <ThemeProvider>
                <PageLayout>
                    <CartProvider>
                        <MainContent />
                    </CartProvider>
                </PageLayout>
            </ThemeProvider>
        </HelmetProvider>
    );
}
